import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { eventsAPI } from '../services/api';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  TablePagination,
  Tooltip,
} from '@mui/material';
const formatARDateTime = (s) => new Intl.DateTimeFormat('es-AR', {
  timeZone: 'America/Argentina/Buenos_Aires',
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
  hour12: false,
}).format(new Date(s));

const PAGE_SIZE = 20;

const Events = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchEvents = async (currentPage) => {
    try {
      setLoading(true);
      const response = await eventsAPI.getAll({
        limit: PAGE_SIZE,
        offset: currentPage * PAGE_SIZE,
      });
      setEvents(response.data.events);
      setTotal(response.data.total);
      setError('');
    } catch (err) {
      setError(t('events.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents(page);
  }, [page]);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>{t('events.title')}</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 600 }}>{t('events.when')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('events.user')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('events.event')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('events.entity')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('events.attributes')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : events.map((ev) => (
                <TableRow key={ev.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatARDateTime(ev.createdAt)}
                  </TableCell>
                  <TableCell>{ev.user?.username ?? '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {ev.eventName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {ev.entityType
                      ? <>{ev.entityType}{ev.entityId ? <Typography component="span" variant="caption" color="text.secondary"> #{ev.entityId.slice(0, 8)}</Typography> : null}</>
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {ev.attributes ? (
                      <Tooltip title={JSON.stringify(ev.attributes, null, 2)} placement="left">
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', cursor: 'default' }}>
                          {JSON.stringify(ev.attributes).slice(0, 60)}
                          {JSON.stringify(ev.attributes).length > 60 ? '…' : ''}
                        </Typography>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </Paper>
    </Box>
  );
};

export default Events;
